import React, { useState } from 'react'
import { useTheme } from '../hooks/useTheme'

const Button = React.forwardRef(
  (
    {
      children,
      variant = 'outlined',
      size = 'small',
      color = 'primary',
      ...props
    },
    ref
  ) => {
    const theme = useTheme()

    const PRIMARY_COLOR = theme.palette.primary.main || '#1976d2'
    const PRIMARY_BORDER = PRIMARY_COLOR || 'rgba(25, 118, 210, 0.5)'

    const SECONDARY_COLOR = theme.palette.secondary.main || '#9c27b0'
    const SECONDARY_BORDER = SECONDARY_COLOR || 'rgba(156, 39, 176, 0.5)'

    const BOX_SHADOW =
      '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)'

    // Get button styles based on variant and color
    const getButtonStyles = () => {
      const baseStyles = {
        outline: 0,
        border: 0,
        margin: 0,
        cursor: 'pointer',
        borderRadius: '4px',
        textTransform: 'uppercase',
        transition:
          'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
      }

      // Size variations
      const sizeStyles = {
        padding: '6px 10px',
        fontSize: '0.63rem',
        minWidth: '4rem',
        letterSpacing: '0.5px'
      }

      // Variant and color combinations
      const variantStyles = {
        outlined: {
          primary: {
            color: PRIMARY_COLOR,
            backgroundColor: 'transparent',
            border: `1px solid ${PRIMARY_BORDER}`,
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              border: '1px solid #1976d2'
            }
          },
          secondary: {
            color: SECONDARY_COLOR,
            border: `1px solid ${SECONDARY_BORDER}`,
            '&:hover': {
              backgroundColor: 'rgba(156, 39, 176, 0.04)',
              border: '1px solid #9c27b0'
            }
          }
        },
        contained: {
          primary: {
            color: '#fff',
            backgroundColor: PRIMARY_COLOR,
            boxShadow: BOX_SHADOW,
            '&:hover': {
              backgroundColor: '#1565c0',
              boxShadow:
                '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)'
            }
          },
          secondary: {
            color: '#fff',
            backgroundColor: SECONDARY_COLOR,
            boxShadow: BOX_SHADOW,
            '&:hover': {
              backgroundColor: '#7b1fa2',
              boxShadow:
                '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)'
            }
          }
        }
      }

      return {
        ...baseStyles,
        ...sizeStyles,
        ...variantStyles[variant][color]
      }
    }

    const styles = getButtonStyles()

    return (
      <button ref={ref} style={styles} type='button' {...props}>
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
